import axios, { AxiosResponse } from "axios"
import * as React from "react"
import { FunctionComponent, useState } from "react"
import { useForm } from "react-hook-form/dist/index.ie11"
import { HideSpinner, ShowSpinner } from "../../../ts/components/spinner"
import { ICommercetoolsCart } from "../../../ts/models/icommercetools-cart"
import { BackButton } from "../../shared/checkout/back-button"
import { IResponseModelState } from "../../../ts/models/response-model-state"
import { validatePostcode } from "../../../ts/functions/form-validation"
import { IFormError } from "../../../ts/models/error/form-error"
import { ShowPopupError } from "../../../ts/components/error-popup"
import { countryList } from "../../../ts/constants/form-values"
import {
	AddressVariantRadios,
	LeitwegIdField,
	deriveAddressVariant,
	normalizeAddressVariant
} from "../../checkout-addresses/components/address-variant-fields"
import { VatField } from "../../checkout-addresses/components/vat-field"

interface IProps {
	Cart: ICommercetoolsCart
}

/**
 * Feldnamen ohne Billing./Shipping.-Präfix, für die dieses Formular eine Inline-Fehleranzeige
 * rendert. Bewusst eine Liste und nicht aus dem Markup abgeleitet: ein vergessener Eintrag
 * erzeugt eine doppelte Meldung, ein vergessener CreateApiError-Aufruf dagegen einen unsichtbaren
 * Fehler. Die Liste fällt also in die harmlose Richtung.
 */
const INLINE_ERROR_FIELDS = [
	"Company",
	"Addition1",
	"Addition2",
	"FirstName",
	"LastName",
	"LeitwegId",
	"VatNo",
	// Zulässig, weil die Vollständigkeitsprüfung in AddAddressesToCartGuest Straße und Hausnummer
	// bei gesetzter Postfachnummer überspringt: aus dem Formular heraus kann ein Fehler zu diesen
	// Feldern also nur entstehen, wenn sie auch gerendert sind. Einzige Ausnahme ist ein
	// handgebauter Request ohne Straße *und* ohne Postfachnummer — dort bleibt die Meldung ohne Feld.
	"StreetName",
	"StreetNumber"
]

/** Wird der Fehler zu diesem ModelState-Key irgendwo am Feld angezeigt? */
function isDisplayedInline(key: string): boolean {
	const field = key.substring(key.lastIndexOf(".") + 1)
	if (field === "Email") {
		// Nur die Gast-E-Mail hat ein Eingabefeld, das Email-Feld von AddressApiModel nicht.
		return key.indexOf("Billing.") < 0 && key.indexOf("Shipping.") < 0
	}
	return INLINE_ERROR_FIELDS.indexOf(field) >= 0
}

export const CheckoutAddressesGuest: FunctionComponent<IProps> = (props: IProps) => {
	const { register, handleSubmit, errors, watch, setValue, getValues } = useForm({
		defaultValues: {
			billing: {
				company: props.Cart?.CurrentAddresses?.BillingAddress?.Company,
				// Die Feldnamen zeigen Richtung API (AddressApiModel.Addition1/2), die Werte kommen
				// aus dem Warenkorb-Modell (CommercetoolsAddress.CompanyAddition1/2). Vor ECOM-3705
				// hießen die Felder hier companyAddition1/2 und banden serverseitig auf nichts.
				Addition1: props.Cart?.CurrentAddresses?.BillingAddress?.CompanyAddition1,
				Addition2: props.Cart?.CurrentAddresses?.BillingAddress?.CompanyAddition2,
				department: props.Cart?.CurrentAddresses?.BillingAddress?.Department,
				industry: props.Cart?.CurrentAddresses?.BillingAddress?.Industry,
				salutation: props.Cart?.CurrentAddresses?.BillingAddress?.Salutation,
				title: props.Cart?.CurrentAddresses?.BillingAddress?.Title,
				firstName: props.Cart?.CurrentAddresses?.BillingAddress?.FirstName,
				lastName: props.Cart?.CurrentAddresses?.BillingAddress?.LastName,
				additionalInfo: props.Cart?.CurrentAddresses?.BillingAddress?.AdditionalInfo,
				streetName: props.Cart?.CurrentAddresses?.BillingAddress?.StreetName,
				streetNumber: props.Cart?.CurrentAddresses?.BillingAddress?.StreetNumber,
				postalCode: props.Cart?.CurrentAddresses?.BillingAddress?.PostalCode,
				city: props.Cart?.CurrentAddresses?.BillingAddress?.City,
				country: props.Cart?.CurrentAddresses?.BillingAddress?.Country,
				vatNo: props.Cart?.CurrentAddresses?.BillingAddress?.VatNo,
				leitwegId: props.Cart?.CurrentAddresses?.BillingAddress?.LeitwegId,
				isPublicAuthority: props.Cart?.CurrentAddresses?.BillingAddress?.LeitwegId
					? "true"
					: "false"
			},
			shipping: {
				company: props.Cart?.CurrentAddresses?.ShippingAddress?.Company,
				Addition1: props.Cart?.CurrentAddresses?.ShippingAddress?.CompanyAddition1,
				Addition2: props.Cart?.CurrentAddresses?.ShippingAddress?.CompanyAddition2,
				department: props.Cart?.CurrentAddresses?.ShippingAddress?.Department,
				industry: props.Cart?.CurrentAddresses?.ShippingAddress?.Industry,
				salutation: props.Cart?.CurrentAddresses?.ShippingAddress?.Salutation,
				title: props.Cart?.CurrentAddresses?.ShippingAddress?.Title,
				firstName: props.Cart?.CurrentAddresses?.ShippingAddress?.FirstName,
				lastName: props.Cart?.CurrentAddresses?.ShippingAddress?.LastName,
				additionalInfo:
					props.Cart?.CurrentAddresses?.ShippingAddress?.AdditionalInfo,
				streetName: props.Cart?.CurrentAddresses?.ShippingAddress?.StreetName,
				streetNumber: props.Cart?.CurrentAddresses?.ShippingAddress?.StreetNumber,
				postalCode: props.Cart?.CurrentAddresses?.ShippingAddress?.PostalCode,
				postfachNo: props.Cart?.CurrentAddresses?.ShippingAddress?.PostfachNo,
				city: props.Cart?.CurrentAddresses?.ShippingAddress?.City,
				country: props.Cart?.CurrentAddresses?.ShippingAddress?.Country,
				shippingtype: props.Cart?.CurrentAddresses?.ShippingAddress?.PostfachNo
					? "postfach"
					: "address"
			},
			email: props.Cart?.CurrentAddresses?.BillingAddress?.GuestEmailAddress,
			useShippingAddress:
				props.Cart?.CurrentAddresses?.IsShippingAddressSameAsBillingAddress !==
					undefined &&
				props.Cart?.CurrentAddresses?.IsShippingAddressSameAsBillingAddress === false
		}
	})

	const [useShippingAddress, setuseShippingAddress] = useState(
		props.Cart?.CurrentAddresses?.IsShippingAddressSameAsBillingAddress !==
			undefined &&
			props.Cart?.CurrentAddresses?.IsShippingAddressSameAsBillingAddress === false
	)
	const type = watch("shipping.shippingtype", "address")

	const [plzError, setPlZError] = useState(false)

	const countryBilling: string = watch("billing.country") // watch selcted country,
	const countryShipping: string = watch("shipping.country") // watch selcted country

	// Adressart „Öffentlicher Auftraggeber" / Leitweg-ID: dieselbe Ableitung wie im Kundenpfad,
	// nur auf den verschachtelten billing-Zweig angewandt. Ein Gast bearbeitet keinen
	// Adressbuch-Eintrag, deshalb kein isEdit und kein isKupManaged.
	const billingVariant = deriveAddressVariant(watch, {
		namePrefix: "billing.",
		fallbackCountry: props.Cart?.CurrentAddresses?.BillingAddress?.Country
	})

	const [modelStateErrors, setModelStateErrors] = useState<IResponseModelState[]>()

	const validateShippingCountry = (value: string) => {
		return validatePlz(countryShipping, value)
	}

	const validateBillingCountry = (value: string) => {
		return validatePlz(countryBilling, value)
	}

	function validatePlz(country: string, value: string) {
		const isValid = validatePostcode(value, country)
		setPlZError(!isValid)
		return isValid
	}

	function onSubmit(data: Record<string, any>) {
		// Dieselbe Submit-Regel wie im Kundenformular, angewandt auf den Rechnungsadress-Zweig.
		normalizeAddressVariant(data.billing, billingVariant, {})

		ShowSpinner()
		axios
			.post(`/umbraco/api/GuestCart/AddAddressesToCart`, data)
			.then((result: AxiosResponse) => {
				HideSpinner()
				window.location.href = "/checkout-teilnehmer"
			})
			.catch((error) => {
				HideSpinner()
				if (!error?.response?.data) {
					return
				}

				const object = error.response.data as IFormError
				setModelStateErrors(object.ValidationErrors)

				if (object.ErrorMessage) {
					ShowPopupError(object.ErrorMessage)
					return
				}

				// GetInvalidModelResponse liefert keine ErrorMessage. Ein Feldfehler ohne
				// Inline-Anzeige wäre damit vollständig unsichtbar: Spinner weg, nichts passiert,
				// der Klick auf „Weiter" sähe wie ein Hänger aus. Beispiel: eine einbuchstabige
				// Abteilung scheitert an ValidateCompanyFields, hat aber kein Fehlerfeld.
				const undisplayed = (object.ValidationErrors ?? []).filter(
					(entry) => !isDisplayedInline(entry.Key)
				)
				if (undisplayed.length > 0) {
					ShowPopupError(undisplayed.map((entry) => entry.Value[0]).join(" "))
				}
			})
	}

	/**
	 * Erwartet den auf das DTO bezogenen Key, z. B. "Billing.Company". Der Vergleich läuft über
	 * das Suffix, weil das Model-Binding den ModelState-Keys je nach Bindung ein Präfix
	 * voranstellt ("model.Billing.Company"). Vorher standen die Präfixe fest im Aufruf.
	 */
	function CreateApiError(key: string) {
		const error = modelStateErrors?.find((x) => x.Key === key || x.Key.endsWith(`.${key}`))
		if (error !== undefined && error !== null) {
			return <div className="form-error">{error.Value[0]}</div>
		}
	}

	const createBillingApiError = (key: string) => CreateApiError(`Billing.${key}`)

	/**
	 * Serverfehler auf einem Feld des DTO-Wurzelobjekts — heute nur die Gast-E-Mail. Die
	 * verschachtelten Adressen sind ausgenommen, weil AddressApiModel selbst ein Email-Feld hat
	 * und der Suffix-Vergleich sonst "Billing.Email" am Gast-Feld anzeigen würde.
	 */
	function CreateRootApiError(key: string) {
		const error = modelStateErrors?.find(
			(x) =>
				(x.Key === key || x.Key.endsWith(`.${key}`)) &&
				x.Key.indexOf("Billing.") < 0 &&
				x.Key.indexOf("Shipping.") < 0
		)
		if (error !== undefined && error !== null) {
			return <div className="form-error">{error.Value[0]}</div>
		}
	}

	return (
		<div className="container">
			<div className="row">
				<div className="col-12 address__form">
					<div className="billing">
						<span className="heading">Gastbestellung</span>
						<p>
							Bitte füllen Sie alle mit <span className="mandatory">*</span>{" "}
							gekennzeichneten Felder aus.
						</p>
					</div>
				</div>
			</div>
			<form onSubmit={handleSubmit(onSubmit)}>
				<div className="address__form">
					<div className="row">
						<div className="col-12">
							<div className="heading">Rechnungsadresse</div>
						</div>
						<div className="col-sm-12">
							<AddressVariantRadios register={register} namePrefix="billing." idScope="billing" />
							<div className="form-group row">
								<label className="col-sm-4 col-form-label">
									Firma / Name des Selbstständigen
									<span className="mandatory">*</span> <br />
									<span className="form-hint">
										(Bitte tragen Sie bei selbstständiger Tätigkeit hier die
										Firmierung / Ihren Namen, z.B. Rechtsanwältin Erika Musterfrau,
										ein.)
									</span>
								</label>
								<div className="col-sm-8">
									<input
										type="text"
										name="billing.company"
										className="form-control"
										placeholder="Firmenname"
										maxLength={35}
										ref={register({ required: true })}
									/>
									{errors?.billing?.company && (
										<div className="form-error">
											Bitte geben Sie Ihren Firmennamen ein.
										</div>
									)}
									{CreateApiError("Billing.Company")}
									<input
										type="text"
										name="billing.Addition1"
										className="form-control mt-3"
										placeholder="Firmenzusatz1"
										maxLength={35}
										ref={register()}
									/>
									{CreateApiError("Billing.Addition1")}
									<input
										type="text"
										name="billing.Addition2"
										className="form-control mt-3"
										placeholder="Firmenzusatz2"
										maxLength={35}
										ref={register()}
									/>
									{CreateApiError("Billing.Addition2")}
								</div>
							</div>
							<div className="form-group row">
								<label className="col-sm-4 col-form-label">Abteilung</label>
								<div className="col-sm-8">
									<input
										type="text"
										name="billing.department"
										className="form-control"
										placeholder="Abteilung"
										maxLength={35}
										ref={register()}
									/>
								</div>
							</div>
							<LeitwegIdField
								register={register}
								errors={errors}
								variant={billingVariant}
								namePrefix="billing."
								createApiError={createBillingApiError}
							/>
							<div className="form-group row">
								<label className="col-sm-4 col-form-label">
									Anrede <span className="mandatory">*</span>
								</label>
								<div className="col-md-5 col-sm-8">
									<select
										name="billing.salutation"
										className="form-control"
										id=""
										ref={register({ required: true })}
									>
										<option value=""></option>
										<option value="F">Frau</option>
										<option value="M">Herr</option>
										<option value="D">Divers</option>
									</select>
									{errors?.billing?.salutation && (
										<div className="form-error">
											Bitte geben Sie Ihre Anrede ein.
										</div>
									)}
								</div>
							</div>
							<div className="form-group row">
								<label className="col-sm-4 col-form-label">Titel</label>
								<div className="col-md-5 col-sm-8">
									<select
										name="billing.title"
										className="form-control"
										id=""
										placeholder="Titel"
										ref={register()}
									>
										<option></option>
										<option>Dr.</option>
										<option>Prof.</option>
										<option>Prof. Dr.</option>
										<option>LL. M.</option>
										<option>Ass. Jur.</option>
									</select>
								</div>
							</div>
							<div className="form-group row">
								<label className="col-sm-4 col-form-label">
									Vorname <span className="mandatory">*</span>
								</label>
								<div className="col-sm-8">
									<input
										type="text"
										name="billing.firstName"
										className="form-control"
										placeholder="Vorname"
										maxLength={30}
										ref={register({ required: true, maxLength: 30 })}
									/>

									{errors?.billing?.firstName && (
										<div className="form-error">
											Der Vorname darf maximal 30 Zeichen lang sein.
										</div>
									)}
									{CreateApiError("Billing.FirstName")}
								</div>
							</div>
							<div className="form-group row">
								<label className="col-sm-4 col-form-label">
									Nachname <span className="mandatory">*</span>
								</label>
								<div className="col-sm-8">
									<input
										type="text"
										name="billing.lastName"
										className="form-control"
										placeholder="Nachname"
										maxLength={30}
										ref={register({ required: true, maxLength: 30 })}
									/>
									{errors?.billing?.lastName && (
										<div className="form-error">
											Der Nachname darf maximal 30 Zeichen lang sein.
										</div>
									)}
									{CreateApiError("Billing.LastName")}
								</div>
							</div>
							<div className="form-group row">
								<label className="col-sm-4 col-form-label">Adresszusatz</label>
								<div className="col-sm-8">
									<input
										type="text"
										name="billing.additionalInfo"
										className="form-control"
										placeholder="Adresszusatz"
										maxLength={81}
										ref={register({ maxLength: 81 })}
									/>
								</div>
							</div>
							<div className="form-group row">
								<label className="col-sm-4 col-form-label">
									Straße / Hausnummer <span className="mandatory">*</span>
								</label>
								<div className="col-sm-5 col-xs-1">
									<input
										type="text"
										name="billing.streetName"
										className="form-control"
										placeholder="Straße"
										maxLength={46}
										ref={register({ required: true })}
									/>
									{errors?.billing?.streetName && (
										<div className="form-error">Bitte geben Sie die Straße ein.</div>
									)}
									{CreateApiError("Billing.StreetName")}
								</div>
								<div className="col-sm-3 col-xs-1">
									<input
										type="text"
										name="billing.streetNumber"
										className="form-control"
										placeholder="Hausnummer"
										ref={register({ required: true })}
									/>
									{errors?.billing?.streetNumber && (
										<div className="form-error">Bitte geben Sie die Hausnummer ein.</div>
									)}
									{CreateApiError("Billing.StreetNumber")}
								</div>
							</div>
							<div className="form-group row">
								<label className="col-sm-4 col-form-label">
									PLZ / Ort<span className="mandatory">*</span>
								</label>
								<div className="col-sm-3 col-xs-1">
									<input
										type="text"
										name="billing.postalCode"
										className="form-control"
										placeholder="PLZ"
										ref={register({
											required: true,
											validate: validateBillingCountry
										})}
									/>
									{errors?.billing?.postalCode && (
										<div className="form-error">
											Bitte geben Sie die valide PLZ ein.
										</div>
									)}
								</div>
								<div className="col-sm-5 col-xs-1">
									<input
										type="text"
										name="billing.city"
										className="form-control"
										placeholder="Ort"
										ref={register({ required: true })}
									/>
									{errors?.billing?.city && (
										<div className="form-error">Bitte geben Sie den Ort ein.</div>
									)}
								</div>
							</div>
							<div className="form-group row">
								<label className="col-sm-4 col-form-label">
									Land: <span className="mandatory">*</span>
								</label>
								<div className="col-md-5 col-sm-8">
									<select
										name="billing.country"
										className="form-control"
										placeholder=""
										ref={register({ required: true })}
									>
										{countryList.map((country) => (
											<option value={country.value}>{country.name}</option>
										))}
									</select>
									{errors?.billing?.country && (
										<div className="form-error">
											Bitte wählen Sie Ihre Stadt aus.
										</div>
									)}
								</div>
							</div>
							<VatField
								register={register}
								errors={errors}
								watch={watch}
								namePrefix="billing."
								fallbackCountry={props.Cart?.CurrentAddresses?.BillingAddress?.Country}
								createApiError={createBillingApiError}
							/>
							<div className="form-group row">
								<label className="col-sm-4 col-form-label">
									E-Mail <span className="mandatory">*</span>
								</label>
								<div className="col-sm-8">
									<input
										type="email"
										name="email"
										className="form-control"
										placeholder="E-Mail"
										maxLength={60}
										ref={register({
											required: true,
											maxLength: 60,
											pattern: /^(([^\s"(),.:;<>@[\\\]]+(\.[^\s"(),.:;<>@[\\\]]+)*)|(".+"))@((\[(?:\d{1,3}\.){3}\d{1,3}])|(([\dA-Za-z\-]+\.)+[A-Za-z]{2,}))$/
										})}
									/>
									{errors?.email && (
										<div className="form-error">
											Bitte geben Sie Ihre E-Mail ein.
										</div>
									)}
									{CreateRootApiError("Email")}
								</div>
							</div>
						</div>
					</div>
				</div>
				<div className="row">
					<div className="col-12">
						<div
							className="check different__address"
							onClick={() => setuseShippingAddress(!useShippingAddress)}
						>
							<span
								className={`check__box check__elm billing__checkbox ${
									useShippingAddress ? "checked" : ""
								}`}
							></span>
							<span className="check__elm checkbox__label">
								abweichende Rechnungsversandadresse
							</span>
							<input
								type="hidden"
								name="useShippingAddress"
								ref={register()}
								value={String(useShippingAddress)}
							/>
						</div>
					</div>
				</div>
				{useShippingAddress && (
					<div className="address__form">
						<div className="row">
							<div className="col-12">
								<div className="heading">Abweichende Rechnungsversandadresse</div>
							</div>
							<div className="col-sm-12">
								<div className="form-group row">
									<label className="col-sm-4 col-form-label">
										Firma / Name des Selbstständigen
										<span className="mandatory">*</span> <br />
										<span className="form-hint">
											(Bitte tragen Sie bei selbstständiger Tätigkeit hier die
											Firmierung / Ihren Namen, z.B. Rechtsanwältin Erika Musterfrau,
											ein.)
										</span>
									</label>
									<div className="col-sm-8">
										<input
											type="text"
											name="shipping.company"
											className="form-control"
											placeholder="Firmenname"
											ref={register({ required: true })}
										/>
										{errors?.shipping?.company && (
											<div className="form-error">
												Bitte geben Sie Ihren Firmennamen ein.
											</div>
										)}
										{CreateApiError("Shipping.Company")}
										<input
											type="text"
											name="shipping.Addition1"
											className="form-control mt-3"
											placeholder="Firmenzusatz1"
											ref={register()}
										/>
										{CreateApiError("Shipping.Addition1")}
										<input
											type="text"
											name="shipping.Addition2"
											className="form-control mt-3"
											placeholder="Firmenzusatz2"
											ref={register()}
										/>
										{CreateApiError("Shipping.Addition2")}
									</div>
								</div>
								<div className="form-group row">
									<label className="col-sm-4 col-form-label">Abteilung</label>
									<div className="col-sm-8">
										<input
											type="text"
											name="shipping.department"
											className="form-control"
											placeholder="Abteilung"
											ref={register()}
										/>
									</div>
								</div>
								<div className="form-group row">
									<label className="col-sm-4 col-form-label">Anrede</label>
									<div className="col-md-5 col-sm-8">
										<select
											name="shipping.salutation"
											className="form-control"
											id=""
											ref={register()}
										>
											<option value=""></option>
											<option value="F">Frau</option>
											<option value="M">Herr</option>
											<option value="D">Divers</option>
										</select>
									</div>
								</div>
								<div className="form-group row">
									<label className="col-sm-4 col-form-label">Titel</label>
									<div className="col-md-5 col-sm-8">
										<select
											name="shipping.title"
											className="form-control"
											id=""
											placeholder="Titel"
											ref={register()}
										>
											<option></option>
											<option>Dr.</option>
											<option>Prof.</option>
											<option>Prof. Dr.</option>
											<option>LL. M.</option>
											<option>Ass. Jur.</option>
										</select>
									</div>
								</div>
								<div className="form-group row">
									<label className="col-sm-4 col-form-label">Vorname</label>
									<div className="col-sm-8">
										<input
											type="text"
											name="shipping.firstName"
											className="form-control"
											placeholder="Vorname"
											ref={register()}
										/>
										{CreateApiError("Shipping.FirstName")}
									</div>
								</div>
								<div className="form-group row">
									<label className="col-sm-4 col-form-label">Nachname</label>
									<div className="col-sm-8">
										<input
											type="text"
											name="shipping.lastName"
											className="form-control"
											placeholder="Nachname"
											ref={register()}
										/>
										{CreateApiError("Shipping.LastName")}
									</div>
								</div>
								<div className="form-group row">
									<label className="col-sm-4 col-form-label"></label>
									<div className="col-sm-8">
										<div
											className="radio-item d-inline"
											onClick={() => setValue("shipping.shippingtype", "address")}
										>
											<input
												type="radio"
												ref={register}
												name="shipping.shippingtype"
												value={"address"}
												checked={getValues("shipping.shippingtype") === "address"}
											/>
											<label>Adresse</label>
										</div>
										<div
											className="radio-item d-inline ml-3"
											onClick={() => setValue("shipping.shippingtype", "postfach")}
										>
											<input
												type="radio"
												ref={register}
												name="shipping.shippingtype"
												value={"postfach"}
												checked={getValues("shipping.shippingtype") === "postfach"}
											/>
											<label>Postfach</label>
										</div>
									</div>
								</div>
								{type === "address" && (
									<React.Fragment>
										<div className="form-group row">
											<label className="col-sm-4 col-form-label">Adresszusatz</label>
											<div className="col-sm-8">
												<input
													type="text"
													name="shipping.additionalInfo"
													className="form-control"
													placeholder="Adresszusatz"
													ref={register()}
												/>
											</div>
										</div>
										<div className="form-group row">
											<label className="col-sm-4 col-form-label">
												Straße / Hausnummer <span className="mandatory">*</span>
											</label>
											<div className="col-sm-5 col-xs-1">
												<input
													type="text"
													name="shipping.streetName"
													className="form-control"
													placeholder="Straße"
													ref={register({ required: true })}
												/>
												{errors?.shipping?.streetName && (
													<div className="form-error">
														Bitte geben Sie die Straße ein.
													</div>
												)}
												{CreateApiError("Shipping.StreetName")}
											</div>
											<div className="col-sm-3 col-xs-1">
												<input
													type="text"
													name="shipping.streetNumber"
													className="form-control"
													placeholder="Hausnummer"
													ref={register({ required: true })}
												/>
												{errors?.shipping?.streetNumber && (
													<div className="form-error">
														Bitte geben Sie die Hausnummer ein.
													</div>
												)}
												{CreateApiError("Shipping.StreetNumber")}
											</div>
										</div>
									</React.Fragment>
								)}

								{type === "postfach" && (
									<div className="form-group row">
										<label className="col-sm-4 col-form-label">
											Postfach<span className="mandatory">*</span>
										</label>
										<div className="col-sm-3 col-xs-1">
											<input
												type="text"
												name="shipping.postfachNo"
												className="form-control"
												placeholder="Postfach"
												ref={register({ required: true })}
											/>
											{errors?.shipping?.postfachNo && (
												<div className="form-error">
													Bitte geben Sie die Postfachnummer ein.
												</div>
											)}
										</div>
									</div>
								)}
								<div className="form-group row">
									<label className="col-sm-4 col-form-label">
										PLZ / Ort<span className="mandatory">*</span>
									</label>
									<div className="col-sm-3 col-xs-1">
										<input
											type="text"
											name="shipping.postalCode"
											className="form-control"
											placeholder="PLZ"
											ref={register({
												required: true,
												validate: validateShippingCountry
											})}
										/>
										{errors?.shipping?.postalCode && (
											<div className="form-error">
												Bitte geben Sie die valide PLZ ein.
											</div>
										)}
									</div>
									<div className="col-sm-5 col-xs-1">
										<input
											type="text"
											name="shipping.city"
											className="form-control"
											placeholder="Ort"
											ref={register({ required: true })}
										/>
										{errors?.shipping?.city && (
											<div className="form-error">Bitte geben Sie den Ort ein.</div>
										)}
									</div>
								</div>
								<div className="form-group row">
									<label className="col-sm-4 col-form-label">
										Land: <span className="mandatory">*</span>
									</label>
									<div className="col-md-5 col-sm-8">
										<select
											name="shipping.country"
											className="form-control"
											id=""
											placeholder=""
											ref={register({ required: true })}
										>
											{countryList.map((country) => (
												<option value={country.value}>{country.name}</option>
											))}
										</select>
										{errors?.shipping?.country && (
											<div className="form-error">
												Bitte wählen Sie Ihre Stadt aus.
											</div>
										)}
									</div>
								</div>
							</div>
						</div>
					</div>
				)}
				<div className="row pb-4 process__nav">
					<div className="col-12 col-sm-3">
						<BackButton url="/checkout-login" buttonText="Zurück" />
					</div>
					<div className="col-12 col-sm-9 text-right">
						<button
							type="submit"
							className="button button__cta button--red order__button"
						>
							<span className="txt">Weiter zu Teilnehmerdaten</span>
							<i className="fas fa-arrow-right icon-fa icon--white"></i>
						</button>
					</div>
				</div>
			</form>
		</div>
	)
}
